import { CustomerDetailDialog } from '@/components/Customers/CustomerDetailDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import type { QuestionnaireSourceRecordArchiveStatus } from '@/contracts/customer-application-binding'
import { type DuplicateCustomerSignal, createDuplicateSignals } from '@/contracts/customers'
import { useAuth } from '@/lib/auth'
import { type ReviewCenterViewMode } from '@/lib/b2b-customer-workflow-ui'
import {
  B2B_PAGE_SIZE_OPTIONS,
  type B2bPageSize,
  REVIEW_CENTER_DEFAULT_STATUS_FILTER,
  REVIEW_CENTER_PAGE_SIZE,
  getB2bPageCount,
  paginateB2bRows,
} from '@/lib/b2b-list-defaults'
import {
  type BusinessRole,
  type CustomerApplication,
  applicationStatusMeta,
  applicationToCustomer,
  applicationToExistingCustomer,
  canAccessReviewCenter,
  getApplicationCustomerMatch,
  getApplicationCustomerStatusLabel,
  getBusinessRole,
  getCustomerForApplication,
  getSalesUserByEmail,
  isReviewableApplicationStatus,
  summarizeApplicationFormType,
} from '@/lib/business-workflow'
import {
  type SourceArchiveOverride,
  type SourceArchiveStatusFilter,
  getApplicationSourceArchiveStatus,
  getApplicationSourceRecordId,
  matchesApplicationArchiveStatus,
  toSourceArchiveOverride,
} from '@/lib/customer-application-archive'
import { archiveQuestionnaireSourceRecord } from '@/lib/data-client/customers'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import { buildDirectReviewQuestionnairePath } from '@/lib/direct-review-links'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import { Archive, ArchiveRestore, CheckCircle2, FileText, RefreshCw, Search, XCircle } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

type ViewMode = ReviewCenterViewMode
type ReviewAction = Extract<CustomerApplication['status'], 'approved' | 'rejected'>

const reviewActionCopy: Record<ReviewAction, { title: string; description: string }> = {
  approved: {
    title: '核准並建立/更新客戶',
    description: '核准後會依目前資料來源回傳結果更新申請狀態與客戶資料。',
  },
  rejected: {
    title: '拒絕申請',
    description: '拒絕後仍保留申請與審核紀錄，方便後續追溯。',
  },
}

const archiveStatusOptions: Array<{ value: SourceArchiveStatusFilter; label: string }> = [
  { value: 'active', label: '未封存' },
  { value: 'archived', label: '已封存' },
  { value: 'all', label: '全部封存狀態' },
]

export default function ReviewCenterRoute() {
  const { user, isLoading } = useAuth()
  const queryClient = useQueryClient()
  const workflowQuery = useBusinessWorkflowDataClientHydration(!isLoading)
  const hydrateWorkflow = useBusinessWorkflowStore((state) => state.hydrateWorkflow)
  const salesUsers = useBusinessWorkflowStore((state) => state.salesUsers)
  const applications = useBusinessWorkflowStore((state) => state.applications)
  const customers = useBusinessWorkflowStore((state) => state.customers)
  const fieldValuesByApplication = useBusinessWorkflowStore((state) => state.fieldValues)
  const updateApplicationStatus = useBusinessWorkflowStore((state) => state.updateApplicationStatus)
  const featureVisibility = useSystemFeatureVisibility()

  const role = getBusinessRole(user, salesUsers)
  const effectiveMode = getEffectiveMode(role)
  const persona = getPersonaForMode(effectiveMode, user?.email, salesUsers)
  const [selected, setSelected] = useState<CustomerApplication | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')
  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null)
  const [reviewError, setReviewError] = useState('')
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false)
  const [completionOpen, setCompletionOpen] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<CustomerApplication['status'] | 'all'>(
    REVIEW_CENTER_DEFAULT_STATUS_FILTER,
  )
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [supervisorFilter, setSupervisorFilter] = useState('all')
  const [archiveStatusFilter, setArchiveStatusFilter] =
    useState<SourceArchiveStatusFilter>('active')
  const [sourceArchiveOverrides, setSourceArchiveOverrides] = useState<
    Record<string, SourceArchiveOverride>
  >({})
  const [archivingSourceRecordId, setArchivingSourceRecordId] = useState<string | null>(null)
  const [archiveError, setArchiveError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<B2bPageSize>(REVIEW_CENTER_PAGE_SIZE)
  const [isRefreshingReviews, setIsRefreshingReviews] = useState(false)

  const ownerOptions = useMemo(
    () =>
      Array.from(
        new Map(
          applications.map((application) => [
            application.submittedByEmail,
            application.submittedByName,
          ]),
        ),
      ).map(([email, name]) => ({ email, name })),
    [applications],
  )
  const supervisorOptions = useMemo(
    () =>
      Array.from(
        new Map(
          applications.map((application) => [
            application.supervisorEmail,
            application.supervisorName,
          ]),
        ),
      ).map(([email, name]) => ({ email, name })),
    [applications],
  )

  const visibleApplications = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    return applications.filter((application) => {
      const scoped = isApplicationInScope(application, effectiveMode, persona)
      const matchesStatus = statusFilter === 'all' || application.status === statusFilter
      const matchesOwner = ownerFilter === 'all' || application.submittedByEmail === ownerFilter
      const matchesSupervisor =
        supervisorFilter === 'all' || application.supervisorEmail === supervisorFilter
      const matchesArchive = matchesApplicationArchiveStatus(
        application,
        archiveStatusFilter,
        sourceArchiveOverrides,
      )
      const matchesKeyword =
        !normalizedKeyword ||
        [
          application.companyName,
          application.formTitle,
          application.submittedByName,
          application.supervisorName,
          application.contactName,
          application.needSummary,
        ]
          .join(' ')
          .toLocaleLowerCase()
          .includes(normalizedKeyword)
      return (
        scoped &&
        matchesStatus &&
        matchesOwner &&
        matchesSupervisor &&
        matchesArchive &&
        matchesKeyword
      )
    })
  }, [
    applications,
    archiveStatusFilter,
    effectiveMode,
    keyword,
    ownerFilter,
    persona,
    sourceArchiveOverrides,
    statusFilter,
    supervisorFilter,
  ])

  const selectedCustomer = useMemo(() => {
    if (!selected) return null
    const reviewer = {
      reviewerEmail: user?.email ?? selected.supervisorEmail,
      reviewerName: user?.name ?? selected.supervisorName,
    }
    const match = getApplicationCustomerMatch(selected, customers)
    return match?.customer
      ? applicationToExistingCustomer({ ...selected, reviewNotes }, match.customer, reviewer)
      : applicationToCustomer({ ...selected, reviewNotes }, reviewer)
  }, [customers, reviewNotes, selected, user?.email, user?.name])
  const selectedComparisonCustomer = useMemo(
    () => (selected ? getApplicationCustomerMatch(selected, customers)?.customer : null),
    [customers, selected],
  )
  const selectedLinkedCustomer = useMemo(
    () => (selected ? getCustomerForApplication(selected.id, customers) : null),
    [customers, selected],
  )
  const selectedFieldValues = selected ? (fieldValuesByApplication[selected.id] ?? []) : []

  const selectedDuplicateSignals = useMemo(
    () =>
      featureVisibility['customers.duplicates'] && selected
        ? mergeDuplicateSignals(
            createDuplicateSignals(
              selected,
              customers.filter((customer) => customer.id !== selectedLinkedCustomer?.id),
            ),
            selected,
            selectedLinkedCustomer,
          )
        : [],
    [customers, featureVisibility, selected, selectedLinkedCustomer],
  )
  const pageCount = getB2bPageCount(visibleApplications, pageSize)
  const paginatedApplications = useMemo(
    () => paginateB2bRows(visibleApplications, currentPage, pageSize),
    [currentPage, pageSize, visibleApplications],
  )
  const paginationResetKey = [
    effectiveMode,
    keyword,
    ownerFilter,
    persona?.email ?? '',
    archiveStatusFilter,
    pageSize,
    statusFilter,
    supervisorFilter,
  ].join('|')

  useEffect(() => {
    void paginationResetKey
    setCurrentPage(1)
  }, [paginationResetKey])

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount))
  }, [pageCount])

  if (isLoading) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  if (!canAccessReviewCenter(user, salesUsers)) {
    return (
      <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-950">沒有審核中心權限</h1>
          <p className="mt-2 text-sm text-slate-500">你的 Email 尚未列入業務或主管名單。</p>
        </div>
      </div>
    )
  }

  function openApplication(application: CustomerApplication) {
    setSelected(application)
    setReviewNotes(application.reviewNotes)
    setReviewError('')
  }

  async function applyStatus(status: ReviewAction) {
    if (!selected) return
    const approved = status === 'approved'
    setReviewError('')
    setIsReviewSubmitting(true)
    try {
      await updateApplicationStatus(selected.id, status, user, reviewNotes)
      setSelected(null)
      setReviewAction(null)
      if (approved) setCompletionOpen(true)
    } catch (caught) {
      setReviewError(formatReviewMutationError(caught))
    } finally {
      setIsReviewSubmitting(false)
    }
  }

  async function updateApplicationSourceArchiveStatus(
    application: CustomerApplication,
    nextArchiveStatus: QuestionnaireSourceRecordArchiveStatus,
  ) {
    const sourceRecordId = getApplicationSourceRecordId(application)
    if (!sourceRecordId) return
    setArchiveError('')
    setArchivingSourceRecordId(sourceRecordId)
    try {
      const record = await archiveQuestionnaireSourceRecord(sourceRecordId, nextArchiveStatus)
      setSourceArchiveOverrides((current) => ({
        ...current,
        [sourceRecordId]: toSourceArchiveOverride(record),
      }))
      await refreshWorkflowData({ resetArchiveOverrides: true })
    } catch (caught) {
      setArchiveError(caught instanceof Error ? caught.message : '無法更新填寫記錄封存狀態。')
    } finally {
      setArchivingSourceRecordId(null)
    }
  }

  async function refreshWorkflowData(options: { resetArchiveOverrides?: boolean } = {}) {
    await queryClient.invalidateQueries({ queryKey: ['business-workflow-data-client'] })
    const result = await workflowQuery.refetch()
    if (result.data) {
      hydrateWorkflow(result.data)
      if (options.resetArchiveOverrides) setSourceArchiveOverrides({})
    }
    setCurrentPage(1)
  }

  async function refreshReviewsList() {
    setIsRefreshingReviews(true)
    try {
      await refreshWorkflowData({ resetArchiveOverrides: true })
    } finally {
      setIsRefreshingReviews(false)
    }
  }

  const canReviewInMode = effectiveMode === 'admin' || effectiveMode === 'manager'

  return (
    <div className="flex flex-col gap-5 p-4 lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">審核中心</h1>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void refreshReviewsList()}
          disabled={isRefreshingReviews}
        >
          <RefreshCw className={`size-4 ${isRefreshingReviews ? 'animate-spin' : ''}`} />
          重新整理
        </Button>
      </div>

      {!canReviewInMode ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          目前是業務視角，只能檢視自己的新客戶申請，不提供主管審核操作。
        </div>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(220px,1.2fr)_repeat(4,minmax(150px,1fr))]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={keyword}
              onChange={(event) => setKeyword(event.currentTarget.value)}
              placeholder="搜尋客戶、問卷、業務、主管、需求"
              className="pl-9"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as CustomerApplication['status'] | 'all')
            }
          >
            <SelectTrigger aria-label="審核狀態">
              <SelectValue placeholder="審核狀態" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部狀態</SelectItem>
              {Object.entries(applicationStatusMeta).map(([status, meta]) => (
                <SelectItem key={status} value={status}>
                  {meta.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger aria-label="業務">
              <SelectValue placeholder="業務" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部業務</SelectItem>
              {ownerOptions.map((owner) => (
                <SelectItem key={owner.email} value={owner.email}>
                  {owner.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={supervisorFilter} onValueChange={setSupervisorFilter}>
            <SelectTrigger aria-label="主管">
              <SelectValue placeholder="主管" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部主管</SelectItem>
              {supervisorOptions.map((supervisor) => (
                <SelectItem key={supervisor.email} value={supervisor.email}>
                  {supervisor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={archiveStatusFilter}
            onValueChange={(value) => setArchiveStatusFilter(value as SourceArchiveStatusFilter)}
          >
            <SelectTrigger aria-label="封存狀態">
              <SelectValue placeholder="封存狀態" />
            </SelectTrigger>
            <SelectContent>
              {archiveStatusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span>目前顯示 {visibleApplications.length} 筆審核申請</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setKeyword('')
              setStatusFilter(REVIEW_CENTER_DEFAULT_STATUS_FILTER)
              setOwnerFilter('all')
              setSupervisorFilter('all')
              setArchiveStatusFilter('active')
              setCurrentPage(1)
            }}
          >
            清除篩選
          </Button>
        </div>
      </section>

      {archiveError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {archiveError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>客戶</TableHead>
                <TableHead>是否已有客戶</TableHead>
                <TableHead>新客戶建檔選擇</TableHead>
                <TableHead>業務</TableHead>
                <TableHead>主管</TableHead>
                <TableHead>狀態</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedApplications.map((application) => {
                const status = applicationStatusMeta[application.status]
                const linkedCustomer = getCustomerForApplication(application.id, customers)
                const duplicateSignals = featureVisibility['customers.duplicates']
                  ? mergeDuplicateSignals(
                      createDuplicateSignals(
                        application,
                        customers.filter((customer) => customer.id !== linkedCustomer?.id),
                      ),
                      application,
                      linkedCustomer,
                    )
                  : []
                const customerStatus = getApplicationCustomerStatusLabel(application, customers)
                const formTypeSummary = summarizeApplicationFormType(application)
                const sourceRecordId = getApplicationSourceRecordId(application)
                const sourceArchiveStatus = getApplicationSourceArchiveStatus(
                  application,
                  sourceArchiveOverrides,
                )
                const isArchived = sourceArchiveStatus === 'archived'
                return (
                  <TableRow key={application.id}>
                    <TableCell className="min-w-[220px]">
                      <div className="font-medium text-slate-950">{application.companyName}</div>
                      <div className="mt-1 text-xs text-slate-500">{application.contactName}</div>
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <Badge
                        variant={
                          customerStatus.label === '尚未更新客戶資料'
                            ? 'secondary'
                            : customerStatus.label === '已更新客戶資料'
                              ? 'success'
                              : 'warning'
                        }
                      >
                        {customerStatus.label}
                      </Badge>
                      <div className="mt-1 text-xs text-slate-500">
                        {customerStatus.description}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[220px]">
                      <div className="text-sm font-medium text-slate-900">{formTypeSummary}</div>
                    </TableCell>
                    <TableCell>{application.submittedByName}</TableCell>
                    <TableCell>{application.supervisorName}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant={status.variant}>{status.label}</Badge>
                        {duplicateSignals.length > 0 ? (
                          <Badge variant="warning">可能重複</Badge>
                        ) : null}
                        {isArchived ? <Badge variant="secondary">已封存</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {isReviewableApplicationStatus(application.status) ? (
                          <Button asChild size="sm" variant="outline">
                            <a
                              href={buildDirectReviewQuestionnairePath({
                                formId: application.formId,
                                email:
                                  application.submittedByEmail ||
                                  application.contactEmail ||
                                  user?.email ||
                                  '',
                                applicationId: application.id,
                                sourceRecordId,
                              })}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <FileText className="size-4" />
                              審核
                            </a>
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={() => openApplication(application)}
                        >
                          查看
                        </Button>
                        {sourceRecordId ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={archivingSourceRecordId === sourceRecordId}
                            onClick={() =>
                              void updateApplicationSourceArchiveStatus(
                                application,
                                isArchived ? 'active' : 'archived',
                              )
                            }
                          >
                            {isArchived ? (
                              <ArchiveRestore className="size-4" />
                            ) : (
                              <Archive className="size-4" />
                            )}
                            {archivingSourceRecordId === sourceRecordId
                              ? '處理中'
                              : isArchived
                                ? '取消封存'
                                : '封存'}
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <B2bPagination
            currentPage={currentPage}
            pageCount={pageCount}
            pageSize={pageSize}
            totalCount={visibleApplications.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>

      <CustomerDetailDialog
        open={Boolean(selected)}
        customer={selectedCustomer}
        sourceApplication={selected}
        comparisonCustomer={selectedComparisonCustomer}
        fieldValues={selectedFieldValues}
        duplicateSignals={selectedDuplicateSignals}
        canEdit={canReviewInMode}
        canEditFilingStatus={false}
        showErpFilingPanel={false}
        onChange={(patch) => {
          if (typeof patch.reviewNotes === 'string') setReviewNotes(patch.reviewNotes)
        }}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
        footerActions={
          canReviewInMode && selected && isReviewableApplicationStatus(selected.status) ? (
            <>
              <Button variant="destructive" onClick={() => setReviewAction('rejected')}>
                <XCircle className="size-4" />
                拒絕
              </Button>
              <Button onClick={() => setReviewAction('approved')} title="核准並建立或更新客戶">
                <CheckCircle2 className="size-4" />
                核准並建立/更新客戶
              </Button>
            </>
          ) : null
        }
      />

      <ReviewActionDialog
        action={reviewAction}
        notes={reviewNotes}
        error={reviewError}
        isSubmitting={isReviewSubmitting}
        onNotesChange={setReviewNotes}
        onOpenChange={(open) => {
          if (!open && !isReviewSubmitting) {
            setReviewAction(null)
            setReviewError('')
          }
        }}
        onConfirm={() => {
          if (reviewAction) void applyStatus(reviewAction)
        }}
      />
      <ReviewCompleteDialog open={completionOpen} onOpenChange={setCompletionOpen} />
    </div>
  )
}

export function ReviewCompleteDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm text-center">
        <div className="relative mx-auto grid size-16 place-items-center rounded-full bg-emerald-100 text-emerald-700">
          <span className="absolute inline-flex size-16 animate-ping rounded-full bg-emerald-300 opacity-30" />
          <CheckCircle2 className="relative size-8" />
        </div>
        <DialogHeader>
          <DialogTitle className="text-center">審核完成</DialogTitle>
          <DialogDescription className="text-center">
            已核准並完成客戶建立或更新，客戶資料已同步到客戶管理清單。
          </DialogDescription>
        </DialogHeader>
        <Button onClick={() => onOpenChange(false)}>知道了</Button>
      </DialogContent>
    </Dialog>
  )
}

function ReviewActionDialog({
  action,
  notes,
  error,
  isSubmitting,
  onNotesChange,
  onOpenChange,
  onConfirm,
}: {
  action: ReviewAction | null
  notes: string
  error: string
  isSubmitting: boolean
  onNotesChange: (notes: string) => void
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  const copy = action ? reviewActionCopy[action] : null
  return (
    <Dialog open={Boolean(action)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? '審核動作'}</DialogTitle>
          <DialogDescription>{copy?.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="review-action-notes">主管審核意見</Label>
          <Textarea
            id="review-action-notes"
            value={notes}
            onChange={(event) => onNotesChange(event.currentTarget.value)}
            placeholder="輸入本次審核理由、需補資料或建檔注意事項。"
            className="min-h-32"
          />
          {error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? '處理中...' : (copy?.title ?? '確認')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function formatReviewMutationError(error: unknown) {
  if (error instanceof Error && error.message.trim()) return error.message
  return '審核動作目前無法完成，請稍後再試。'
}

function getEffectiveMode(role: BusinessRole): ViewMode {
  if (role === 'admin') return 'admin'
  return 'manager'
}

function getPersonaForMode(
  viewMode: ViewMode,
  email: string | null | undefined,
  salesUsers: ReturnType<typeof useBusinessWorkflowStore.getState>['salesUsers'],
) {
  const current = getSalesUserByEmail(email, salesUsers)
  if (viewMode === 'manager') {
    return current?.role === 'manager'
      ? current
      : salesUsers.find((salesUser) => salesUser.role === 'manager')
  }
  return current
}

function isApplicationInScope(
  application: CustomerApplication,
  viewMode: ViewMode,
  persona: ReturnType<typeof getPersonaForMode>,
) {
  if (viewMode === 'admin') return true
  if (viewMode === 'manager') return true
  void application
  return Boolean(persona)
}

function mergeDuplicateSignals(
  calculatedSignals: DuplicateCustomerSignal[],
  application: CustomerApplication,
  currentCustomer?: ReturnType<typeof getCustomerForApplication> | null,
) {
  const savedSignals = getApplicationSavedDuplicateSignals(application)
  const signalsByKey = new Map<string, DuplicateCustomerSignal>()
  for (const signal of filterSelfDuplicateSignals(
    [...savedSignals, ...calculatedSignals],
    currentCustomer,
  )) {
    const key = [
      signal.source ?? '',
      signal.field,
      signal.value,
      signal.matchedCustomerId,
      signal.matchedCustomerCode ?? '',
    ].join('|')
    signalsByKey.set(key, signal)
  }
  return Array.from(signalsByKey.values())
}

function filterSelfDuplicateSignals(
  signals: DuplicateCustomerSignal[],
  currentCustomer?: ReturnType<typeof getCustomerForApplication> | null,
) {
  if (!currentCustomer) return signals
  return signals.filter((signal) => signal.matchedCustomerId !== currentCustomer.id)
}

function getApplicationSavedDuplicateSignals(application: CustomerApplication) {
  const rawSignals = (
    application as CustomerApplication & {
      duplicateSignals?: DuplicateCustomerSignal[]
    }
  ).duplicateSignals
  return Array.isArray(rawSignals) ? rawSignals : []
}

function B2bPagination({
  currentPage,
  pageCount,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: {
  currentPage: number
  pageCount: number
  pageSize: B2bPageSize
  totalCount: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: B2bPageSize) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
      <span>
        每頁 {pageSize} 筆，共 {totalCount} 筆，第 {currentPage} / {pageCount} 頁
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value) as B2bPageSize)}
        >
          <SelectTrigger className="h-8 w-[112px]" aria-label="每頁筆數">
            <SelectValue placeholder="每頁筆數" />
          </SelectTrigger>
          <SelectContent>
            {B2B_PAGE_SIZE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                每頁 {option} 筆
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        >
          上一頁
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
        >
          下一頁
        </Button>
      </div>
    </div>
  )
}

function MiniInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 break-words font-medium text-slate-900">{value}</div>
    </div>
  )
}

function ReadonlyMiniInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-slate-600 opacity-90">
      <div className="text-xs">{label}（只讀）</div>
      <div className="mt-1 break-words font-medium">{value}</div>
    </div>
  )
}
